import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Button from "../../component/common/Button";
import Input from "../../component/common/Input";
import { ReactComponent as BlackUserIcon } from "../../assets/black user.svg";
import { ReactComponent as AlternateIcon } from "../../assets/@.svg";
import { ReactComponent as PhoneIcon } from "../../assets/phone.svg";
import { toast } from "react-toastify";
import httpServices from "../../services/httpServices";
import { GoogleLogin } from "@react-oauth/google";
import { Link } from 'react-router-dom';
import CryptoJS from 'crypto-js';

// Define the validation schema using Yup
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Full name is required"),
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const UserSignUp = () => {
  const navigate = useNavigate();
  const [terms, setTerms] = useState(false);
  // Setup react-hook-form with yupResolver
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  // Handle form submission
  const onSubmit = async (data) => {
    const { confirmPassword, ...rest } = data;

    console.log('rest',rest)

    const hashedPassword = CryptoJS.MD5(data.password).toString(CryptoJS.enc.Base64);

    rest.password = hashedPassword

    const toastId = toast.loading(`Creating your account...`);
    try {
      const { response, error, loading } = await httpServices.post(
        "/users/register",
        {
          ...rest,
          userType: 0,
        }
      );
      if (error) {
        throw error;
      }
      if (response.status === 201) {
        toast.update(toastId, {
          render: "Registration successful",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        navigate("/login");
      }
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Something went wrong",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-white p-[10px] sm:p-10">
      <div className="card w-[491px]">
        <h1 className="heading">Sign in ZoomVenues 🤝</h1>
        <p className="paragraph p-0 mb-4">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Turpis morbi
          pulvinar venenatis non.
        </p>
        <GoogleLogin
          onSuccess={(credentialResponse) => {
            console.log(credentialResponse);
          }}
          onError={() => {
            console.log("Login Failed");
          }}
          useOneTap
        />
        <span className="w-full flex justify-center text-blue-500 text-sm py-3">
          OR
        </span>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
          <div className="space-y-6">
            {/* Full Name Input */}
            <Input
              Icon={BlackUserIcon}
              label={"Full Name"}
              placeholder={"Enter full name"}
              {...register("name")}
              inputClasses={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}

            {/* Email Input */}
            <Input
              Icon={AlternateIcon}
              label={"Email"}
              placeholder={"Email address"}
              {...register("email")}
              inputClasses={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email.message}</p>
            )}

            {/* Mobile Input */}
            <Input
              Icon={PhoneIcon}
              label={"Mobile"}
              type="number"
              placeholder={"Mobile number"}
              {...register("phoneNumber")}
              inputClasses={errors.phoneNumber ? "border-red-500" : ""}
            />
            {errors.phoneNumber && (
              <p className="text-red-500 text-sm">
                {errors.phoneNumber.message}
              </p>
            )}
            <Input
              // Icon={LockIcon}
              label={"Password"}
              type={"password"}
              placeholder={"Password (min. 8 characters)"}
              {...register("password")}
            />
            {errors.password && (
              <p className="text-red-500 text-sm">{errors.password.message}</p>
            )}

            <Input
              // Icon={LockIcon}
              label={"Confirm Password"}
              type={"password"}
              placeholder={"Retype password"}
              {...register("confirmPassword")}
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="inline-flex space-x-2 mt-[44px] items-center">
            <label
              className="relative flex items-center rounded-full cursor-pointer"
              htmlFor="link"
            >
              <input
                type="checkbox"
                className="relative h-4 w-4 cursor-pointer outline-300-gray rounded-md block"
                id="link"
                onClick={() => setTerms(!terms)}
              />
            </label>
            <label
              className="mt-px font-light text-gray-700 cursor-pointer select-none"
              htmlFor="link"
            >
              <p className="flex text-[13px] font-medium leading-relaxed text-900-gray">
                I agree with the
                <Link
                  to="/terms-and-conditions"
                  className="block text-[13px] font-medium leading-relaxed text-[#306BFF] underline ml-1"
                  style={{ textDecoration: "underline" }}
                >
                  &nbsp;Terms & Conditions
                </Link>
                .
              </p>
            </label>
          </div>

          {/* Submit Button */}
          <Button
            content="Sign In"
            classes={`mt-[19px] hover:bg-[#3872A5] duration-300 justify-center ${
              !terms ? "opacity-50" : ""
            }`}
            type="submit"
            disabled={!terms}
          />
        </form>
      </div>
    </div>
  );
};

export default UserSignUp;
